define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../Util/json2Array',
    '../Util/array2Json',
    '../System/ColorObj',
    '../System/Vec3Obj',
    '../Enum/EnumEditMode',
    '../Enum/EnumPathPlayStatus',
    '../Enum/EnumPathPlayMode'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    json2Array,
    array2Json,
    ColorObj,
    Vec3Obj,
    EnumPathEditMode,
    EnumPathPlayStatus,
    EnumPathPlayMode
) {
    'use strict';

    /**
     * 路径编辑。
     * <br /><br />
     * <div align='center'>
     * <img src='images/Path.gif' width='900' height='650' /><br />
     * 路径编辑效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/Path.html|CooWasm Path demo}
     * @alias Path
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumEditMode} [props.editMode=EnumEditMode.EDIT] 编辑状态，如果waypoints为空，则需要通过鼠标点击场景创建；如果waypoints不为空，则为展示模式。
     * @param {Array.<WayPoint>} [props.waypoints=null] 关键点数组（新）。
     * @param {ColorObj} [props.pointColor=new ColorObj(1.0, 1.0, 1.0, 1.0)] 关键点颜色。
     * @param {ColorObj} [props.lineColor=new ColorObj(1.0, 1.0, 0.0, 0.9)] 路径线颜色。
     * @param {Number} [props.lineWidth=10.0] 路径线宽度。
     * @param {String} [props.textureUrl] 可贴纹理路径。
     * @param {Number} [props.textureScale=1] 纹理密度。
     * @param {Number} [props.speedSpeed=5.0] 纹理运动速度，单位m/s。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @param {CallbackBoolean} [editEndCallback=null] 编辑模式结束回调函数。
     * @param {CallbackString} [tipsCallback=null] 提示文字回调函数。
     * @param {CallbackObj} [pointEditCallback=null] 编辑点回调函数。
     * @example
     * var Path = new CooWasm.Path({},(state)=>{console.log("load status:" + state)});
     */
    class Path {
        /**
         * @description 编辑状态，如果waypoints为空，则需要通过鼠标点击场景创建；如果waypoints不为空，则为展示模式。
         * @type {EnumEditMode}
         */
        editMode;
        /**
         * @description 关键点数组（新）。
         * @type {Array.<WayPoint>}
         */
        waypoints;
        /**
         * @description 关键点颜色。
         * @type {ColorObj}
         */
        pointColor;
        /**
         * @description 路径线颜色。
         * @type {ColorObj}
         */
        lineColor;
        /**
         * @description 路径线宽度。
         * @type {Number}
         */
        lineWidth;
        /**
         * @description 可贴纹理路径。
         * @type {String}
         */
        textureUrl;
        /**
         * @description 纹理密度。
         * @type {Number}
         */
        textureScale;
        /**
         * @description 纹理运动速度，单位m/s。
         * @type {Number}
         */
        textureSpeed;
        /**
         * @description 显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true;
        /**
         * @description 编辑模式结束回调函数。
         * @type {CallbackBoolean}
         */
        editEndCallback
        /**
         * @description 提示文字回调函数。
         * @type {CallbackString}
         */
        tipsCallback
        /**
        * @description 编辑点回调函数。
        * @type {CallbackObj}
        */
        pointEditCallback
        /**
         * @description 路径对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null;
        /**
         * @description 路径组件uuid。
         * @type {String}
         * @readonly
         */
        componentUuid = null;
        /**
         * @description ai属性。
         * @type {String}
         * @readonly
         */
        aiComProps;
        /**
         * @description 绑定的可移动对象。
         * @type {Array.<Model|GeoCuboid|GeoSphere>}
         * @readonly
         */
        bindGeoObjects = [];
        /**
         * @description Path在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Path"
        static avatarType = "deSMP.Avatar.EmptyAvatar"
        static componentType = "deSMP.AvatarComponent.PathComponent"

        constructor(props, callback, editEndCallback, tipsCallback, pointEditCallback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            this.editEndCallback = defaultValue(editEndCallback, null)
            this.tipsCallback = defaultValue(tipsCallback, null)
            this.pointEditCallback = defaultValue(pointEditCallback, null)
            //create avatars
            var aCategory = encode("deSMP.Avatar")
            var aName = encode("EmptyAvatar")
            var cCategory = encode("deSMP.AvatarComponent")
            var cName = encode("PathComponent")
            var aProps = ""
            var cProps = ""
            this.aiComProps = ""
            aProps += this.toStr("bVisible", props.bVisible, "Visible")

            cProps += this.toStr("editMode", props.editMode, "EditMode")
            if (defined(props.waypoints)) {
                this.waypoints = props.waypoints
                cProps += "WayPoints:" + array2Json("COMPOSITE", props.waypoints) + "|"
            }
            
            cProps += this.toColorStr("pointColor", props.pointColor, "PointColor")
            cProps += this.toColorStr("lineColor", props.lineColor, "LineColor")
            cProps += this.toStr("lineWidth", props.lineWidth, "LineWidth")
            cProps += this.toStr("textureUrl", props.textureUrl, "ImageUrl")
            cProps += this.toStr("textureScale", props.textureScale, "Scale")
            cProps += this.toStr("textureSpeed", props.textureSpeed, "Speed")

            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(encode(""), aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), encode(""), encode(""), this.bVisible, true)
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentUuid = idArray[1]
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback) && defined(this.waypoints) && this.waypoints.length > 0) {
                        callback(state)
                    }
                    Element.prototype.Map.paths.push(this)
                })
                callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(this.editEndCallback)) {
                        this.editEndCallback(state)
                    }
                })
                callbackSystem.register("Tips", this.avatarUuid, (info) => {
                    //this.getProperties()
                    if (defined(this.tipsCallback)) {
                        this.tipsCallback(info)
                    }
                })
                callbackSystem.register("PointEdit", this.avatarUuid, (obj) => {
                    this.getProperties()
                    if (defined(this.pointEditCallback)) {
                        const jsonObj = JSON.parse(obj)
                        this.pointEditCallback(jsonObj)
                    }
                })
                //console.log("Path avatarUuid:" + this.avatarUuid + " componentUuid:" + this.componentUuid);
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {EnumPathEditMode} [props.editMode] 编辑状态，如果waypoints为空，则需要通过鼠标点击场景创建；如果waypoints不为空，则为展示模式。
         * @param {Array.<WayPoint>} [props.waypoints] 关键点数组（新）。
         * @param {ColorObj} [props.pointColor] 关键点颜色。
         * @param {ColorObj} [props.lineColor] 路径线颜色。
         * @param {Number} [props.lineWidth] 路径线宽度。
         * @param {String} [props.textureUrl] 可贴纹理路径。
         * @param {Number} [props.textureScale] 纹理密度。
         * @param {Number} [props.speedSpeed] 纹理运动速度，单位m/s。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback] 加载成功后触发的函数回调。
         * @param {CallbackBoolean} [editEndCallback] 编辑模式结束回调函数。
         * @param {CallbackString} [tipsCallback] 提示文字回调函数，可不使用自带提示文字，方便更丰富的文字样式配置。
         * @param {CallbackObj} [pointEditCallback] 编辑点回调函数。
         * @example
         * Path.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props, callback, editEndCallback, tipsCallback, pointEditCallback) {
            let avatarProps = ""
            let componentProps = ""
            this.editEndCallback = defaultValue(editEndCallback, this.editEndCallback)
            this.tipsCallback = defaultValue(tipsCallback, this.tipsCallback)
            this.pointEditCallback = defaultValue(pointEditCallback, this.pointEditCallback)
            if (defined(props)) {
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")

                componentProps += this.toStr("editMode", props.editMode, "EditMode")
                if (defined(props.waypoints)) {
                    this.waypoints = props.waypoints;
                    componentProps += "WayPoints:" + array2Json("COMPOSITE", props.waypoints) + "|"
                }
                componentProps += this.toColorStr("pointColor", props.pointColor, "PointColor")
                componentProps += this.toColorStr("lineColor", props.lineColor, "LineColor")
                componentProps += this.toStr("lineWidth", props.lineWidth, "LineWidth")
                componentProps += this.toStr("textureUrl", props.textureUrl, "ImageUrl")
                componentProps += this.toStr("textureScale", props.textureScale, "Scale")
                componentProps += this.toStr("textureSpeed", props.textureSpeed, "Speed")
                callbackSystem.register("UpdateAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(this.editEndCallback)) {
                        this.editEndCallback(state)
                    }
                })
                callbackSystem.register("Tips", this.avatarUuid, (info) => {
                    this.getProperties()
                    if (defined(this.tipsCallback)) {
                        this.tipsCallback(info)
                    }
                })
                callbackSystem.register("PointEdit", this.avatarUuid, (obj) => {
                    this.getProperties()
                    if (defined(pointEditCallback)) {
                        const jsonObj = JSON.parse(obj)
                        this.pointEditCallback(jsonObj)
                    }
                })

                Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentUuid), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        toColorStr(memName, value, key){
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toHexStr() + "|"
                } else {
                    if (typeof value === 'string') {
                        let color = ColorObj.fromCssColorString(value)
                        if (defined(color)) {
                            value = color.toHexStr()
                            this[memName] = value
                            result = key + ":" + value + "|"
                        } else {
                            console.warn(`Invalid color value: ${value}. Using default value.`)
                        }
                    }
                }
            }
            return result;
        }
        array2Json(arr, radii) {
            if (!Array.isArray(arr) || arr.length === 0) {
                throw new Error('Input must be a non-empty array of Vec3Obj')
            }

            const waypoints = arr.map((vec3, index) => {
                if (!(vec3 instanceof Vec3Obj)) {
                    throw new Error('Each item must be an instance of Vec3Obj')
                }
                const position = vec3.x + "," + vec3.y + "," + vec3.z
                const orientation = "0,0,0";
                const radius = radii && radii[index] !== undefined ? radii[index] : 0
                return {
                    position: position,
                    orientation: orientation,
                    radius: radius
                }
            })

            const jsonObj = {
                waypoints: waypoints
            }
            return JSON.stringify(jsonObj)
        }
        json2Array(jsonString, type) {//type 0:position 1:radius 2:orientation
            try {
                // 解析JSON字符串
                const jsonObj = JSON.parse(jsonString)

                // 检查JSON对象是否有waypoints属性，且它是一个数组
                if (!jsonObj.waypoints || !Array.isArray(jsonObj.waypoints)) {
                    throw new Error('Invalid JSON format: missing or invalid waypoints array')
                }
                if (type == 0) {
                    // 提取并转换position数据
                    const positionArray = jsonObj.waypoints.map(waypoint => {
                        if (typeof waypoint.position !== 'string') {
                            throw new Error('Invalid position format')
                        }

                        const [x, y, z] = waypoint.position.split(',').map(Number);

                        if (isNaN(x) || isNaN(y) || isNaN(z)) {
                            throw new Error('Invalid position values')
                        }

                        return new Vec3Obj(x, y, z)
                    });

                    return positionArray
                } else if (type == 1) {
                    // 提取并转换radius数据
                    const radiusArray = jsonObj.waypoints.map(waypoint => {
                        if (typeof waypoint.radius !== 'number') {
                            throw new Error('Invalid radius format')
                        }

                        return waypoint.radius
                    });

                    return radiusArray
                } else if (type == 2) {
                    // 提取并转换orientation数据
                    const orientationArray = jsonObj.waypoints.map(waypoint => {
                        if (typeof waypoint.orientation !== 'string') {
                            throw new Error('Invalid orientation format')
                        }

                        const [x, y, z] = waypoint.orientation.split(',').map(Number)

                        if (isNaN(x) || isNaN(y) || isNaN(z)) {
                            throw new Error('Invalid orientation values')
                        }

                        return new Vec3Obj(x, y, z)
                    });

                    return orientationArray
                }
            } catch (error) {
                console.error('Error parsing JSON or converting to array:', error.message)
                return null
            }
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * Path.locate((state)=>{console.log("locate status:" + state)});
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            });
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * Path.getProperties();
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false;
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                this.componentUuid = uuid
                for (let z = 0; z < propSize; ++z) {
                    let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                    let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                    let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                    if (name == "EditMode") {
                        this.editMode = value
                    } else if (name == "WayPoints") {
                        this.waypoints = json2Array(value)
                    } else if (name == "PointColor") {
                        this.pointColor = CooWasm.ColorObj.fromCssColorString(value)
                    } else if (name == "LineColor") {
                        this.lineColor = CooWasm.ColorObj.fromCssColorString(value)
                    } else if (name == "LineWidth") {
                        this.lineWidth = Number(value)
                    } else if (name == "ImageUrl") {
                        this.textureUrl = String(value)
                    } else if (name == "Scale") {
                        this.textureScale = Number(value)
                    } else if (name == "Speed") {
                        this.textureSpeed = Number(value)
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true;
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * Path = Path.destroy((state)=>{console.log("destroy status:" + state)});
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!");
                return null;
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state);
                }
                Element.prototype.Map.paths = Element.prototype.Map.paths.filter(item => item !== this);
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid);
                callbackSystem.remove("UpdateAvatarAndComponent", this.avatarUuid);
                callbackSystem.remove("EditEnd", this.avatarUuid)
                callbackSystem.remove("Tips", this.avatarUuid)
                callbackSystem.remove("PointEdit", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar");
                callbackSystem.remove("RemoveAvatar", this.avatarUuid);
                this.avatarUuid = null;
                this.componentUuid = null;
            });
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid));
            return null;
        }
    }
    return Path;
});